import type Quill from '@kim/quill';
import type { ChipEmbedValue } from '@src/common/editor/blots/chipEmbed';
import type { MentionBlotValue } from '@src/common/editor/blots/mentionBlot';
import type { MentionSearchType } from './types';

/**
 * 根据光标所在 slot 的 placeholder 判断 @ 搜索类型。
 * 光标不在任何 slot 内时返回 'usergroup'（搜索人和群）。
 */
export function getSlotMentionType(
  quill: Quill,
  index: number,
): MentionSearchType {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [leaf] = quill.getLeaf(index) as any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parentBlot = leaf?.parent as any;
  if (parentBlot?.statics?.blotName === 'slot') {
    const dom = parentBlot.domNode as HTMLElement;
    const explicit = dom.dataset.mentionType as
      | 'user'
      | 'group'
      | 'usergroup'
      | undefined;
    if (explicit) return explicit;
  }
  return 'usergroup';
}

/**
 * 检测当前光标前是否存在未完成的 @ 触发词。
 * 直接在 Quill 模型中从光标向左扫描，避免 getText() 对 embed blot
 * 返回字符数与模型位置不一致时导致的索引偏移。
 */
export function detectMentionTrigger(
  quill: Quill,
): { atIndex: number; query: string } | null {
  const sel = quill.getSelection();
  if (!sel) return null;
  const cursorIndex = sel.index;

  const cursorSlot = (quill.getFormat(cursorIndex) as Record<string, unknown>)[
    'slot'
  ] as string | undefined;

  for (let i = cursorIndex - 1; i >= 0; i--) {
    const charSlot = (quill.getFormat(i, 1) as Record<string, unknown>)[
      'slot'
    ] as string | undefined;
    if (charSlot !== cursorSlot) break;

    const char = quill.getText(i, 1);
    if (/\s/.test(char)) break;
    if (char === '@') {
      if (i > 0 && /[@\w]/.test(quill.getText(i - 1, 1))) break;
      const query = quill.getText(i + 1, cursorIndex - i - 1);
      return { atIndex: i, query };
    }
  }
  return null;
}

/**
 * 从 Quill delta 构建最终消息字符串（含 chip 与 @ 提及的 md 链接）。
 */
export function buildMessageFromDelta(
  quill: Quill,
  chipValues: Record<string, string>,
): string {
  const delta = quill.getContents();
  let result = '';
  for (const op of delta.ops) {
    if (typeof op.insert === 'string') {
      result += op.insert;
    } else if (op.insert && typeof op.insert === 'object') {
      const ins = op.insert as Record<string, unknown>;

      const chipEmbed = ins['chip-embed'] as ChipEmbedValue | undefined;
      if (chipEmbed) {
        result += chipValues[chipEmbed.key] || chipEmbed.placeholder;
        continue;
      }

      const mentionResult = ins['mention-result'] as
        | MentionBlotValue
        | undefined;
      if (mentionResult) {
        const scheme =
          mentionResult.sourceType === 'AGENT'
            ? 'user'
            : mentionResult.entityType === 'GROUP'
            ? 'group'
            : mentionResult.entityType === 'MSG_BOT'
              ? 'bot'
              : 'user';
        result += ` [@${mentionResult.name}](${scheme}:${mentionResult.id}) `;
      }
    }
  }
  return result.replace(/\n$/, '').trim();
}
